/**
 * ModifyCampaignPage — Page shell for editing an existing Value Matching campaign.
 * Data flow:
 *   URL params → parallel fetch (useCampaignConfiguration + useCampaignDetails)
 *     → useCampaignForm(mode:'modify', initialData) → CampaignForm + submit via useModifyCampaign
 * Sections rendered (top to bottom):
 *   1. Page header (H1 with translated title)
 *   2. CampaignStatusSection (campaignId, version, status badge)
 *   3. Version-conflict Alert (when the backend reports HTTP 409)
 *   4. Generic submit-error Alert (when `error` is set and no version conflict)
 *   5. ConflictTable inside a warning Alert (when conflicting ASINs are returned)
 *   6. CampaignForm (all editable fields for the given campaignType)
 *   7. Cancel (→ view page) + Save Changes (primary, loading=submitting) buttons
 * Success path:
 *   On `success=true`, navigates to the campaign-type list page (mirrors CreateCampaignPage
 *   and the list page pattern).
 */

import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@amzn/awsui-components-core/header';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import Alert from '@amzn/awsui-components-core/alert';
import Spinner from '@amzn/awsui-components-core/spinner';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { useCampaignConfiguration } from 'src/hooks/useCampaignConfiguration';
import { useCampaignDetails } from 'src/hooks/useCampaignDetails';
import { useCampaignForm } from 'src/hooks/useCampaignForm';
import { useModifyCampaign } from 'src/hooks/useModifyCampaign';
import { useTranslate } from 'src/hooks/useTranslate';
import {
    buildRoute,
    CAMPAIGN_CONFIG,
    SLUG_TO_CAMPAIGN_TYPE,
    VALUE_MATCHING_BASE,
} from 'src/routes/valueMatching/routes';
import { CampaignStatus, CampaignType, OperationType } from 'src/@types/valueMatching/enums';
import { logError, trackClick, PAGE_IDS } from 'src/utils/monitoring';
import { CampaignForm } from 'src/components/organisms/CampaignForm';
import CampaignStatusSection from 'src/components/organisms/CampaignForm/sections/CampaignStatusSection';
import { FieldKey } from 'src/utils/valueMatching/form/formValidationUtils';
import ConflictTable from 'src/components/molecules/ConflictTable';

const MODIFY_CAMPAIGN_BUNDLE = 'pages.modifyCampaign';
const VALIDATION_BUNDLE = 'shared.campaignValidation';
const MERCHANT_GROUP_TYPES = new Set([CampaignType.NO_COST_EMI, CampaignType.DEAL]);

/**
 * Statuses that allow modification.
 * only SCHEDULED, PROPAGATING (transitional), and ACTIVE
 * campaigns can be modified. PAUSED_FUNDS_DEPLETED is NOT modifiable.
 */
const MODIFIABLE_STATUSES = new Set<CampaignStatus>([
    CampaignStatus.SCHEDULED,
    CampaignStatus.PROPAGATING,
    CampaignStatus.ACTIVE,
]);

const ModifyCampaignPage: FC = () => {
    const {
        campaignType: slug = '',
        campaignId,
        version,
    } = useParams<{ campaignType: string; campaignId: string; version: string }>();

    const navigate = useNavigate();
    const { translate } = useTranslate(MODIFY_CAMPAIGN_BUNDLE);
    const { translate: translateValidation } = useTranslate(VALIDATION_BUNDLE);

    // Resolve the campaign-type enum from the URL slug. Both data hooks below
    // accept `undefined` safely while the slug is unknown or invalid.
    const campaignType = SLUG_TO_CAMPAIGN_TYPE[slug];
    const showMerchantGroup = campaignType ? MERCHANT_GROUP_TYPES.has(campaignType) : false;
    const campaignTypeName = campaignType ? CAMPAIGN_CONFIG[campaignType].displayName : slug;

    // ── Parallel fetch — both hooks are called unconditionally (React rules).
    const {
        config,
        loading: configLoading,
        error: configError,
    } = useCampaignConfiguration(slug, OperationType.MODIFY_CAMPAIGN);

    const {
        campaignData,
        loading: detailsLoading,
        error: detailsError,
        refetch: refetchDetails,
    } = useCampaignDetails(campaignId, version, campaignType);

    // ── Form state — pre-populated from the fetched campaign details.
    const form = useCampaignForm({
        config,
        campaignType,
        showMerchantGroup,
        mode: 'modify',
        initialData: campaignData ?? undefined,
    });

    // ── Submission state — passes existingData through so the backend can
    // detect concurrent edits via campaignId + version. `campaignData` may be
    // `null` while the details fetch is in-flight; the hook's submit guard
    // short-circuits in that case. The loading gate below also prevents the
    // user from clicking submit before the data resolves.
    const {
        submitModification,
        submitting,
        error: submitError,
        fieldErrors,
        markTouched,
        success,
        conflictingCampaignASINs,
        versionConflict,
        reloadCampaign,
    } = useModifyCampaign({
        form,
        config,
        campaignType,
        existingData: campaignData,
        refetchDetails,
    });

    // Navigate to the list page once a modification succeeds.
    const listRoute = campaignType ? buildRoute.list(campaignType) : VALUE_MATCHING_BASE;
    useEffect(() => {
        if (success) {
            navigate(listRoute);
        }
    }, [success, navigate, listRoute]);

    // Cancel returns to the view page for this campaign. When any URL piece is
    // missing (pre-resolution or invalid slug) we fall back to the Value Matching root.
    const handleCancel = () => {
        trackClick('AbandonForm', { page: PAGE_IDS.MODIFY_CAMPAIGN, ...(campaignType && { campaignType }) });
        if (campaignType && campaignId && version) {
            navigate(buildRoute.view(campaignType, campaignId, version));
        } else {
            navigate(VALUE_MATCHING_BASE);
        }
    };

    const translatedFieldErrors = useMemo<Partial<Record<FieldKey, string>>>(() => {
        const result: Partial<Record<FieldKey, string>> = {};
        (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach((key) => {
            const msg = fieldErrors[key];
            if (msg) result[key] = translateValidation(msg.key, msg.params);
        });
        return result;
    }, [fieldErrors, translateValidation]);

    const pageHeader = (
        <Header variant="h1">
            {translate('modify_title', { campaignType: campaignTypeName })}
        </Header>
    );

    // ── Loading gate — show a spinner while either fetch is in-flight.
    if (configLoading || detailsLoading) {
        return (
            <ContentLayout header={pageHeader}>
                <Box textAlign="center" padding={{ top: 'xxl' }}>
                    <Spinner size="large" />
                </Box>
            </ContentLayout>
        );
    }

    // ── Error gates — show the page header even on error so the user keeps context.
    if (configError) {
        return (
            <ContentLayout header={pageHeader}>
                <Alert type="error" header={translate('error_loading_config')}>
                    {configError}
                </Alert>
            </ContentLayout>
        );
    }

    if (detailsError) {
        return (
            <ContentLayout header={pageHeader}>
                <Alert type="error" header={translate('error_loading_details')}>
                    {detailsError}
                </Alert>
            </ContentLayout>
        );
    }

    // Defensive: by this point the loading/error gates guarantee both campaignData
    // and campaignType are resolved. If either is missing, bail out silently.
    if (!campaignData || !campaignType) {
        return null;
    }

    // ── Status gate — block modification for non-actionable campaigns ────
    // If the user navigates directly to the modify URL for a campaign with a
    // non-modifiable status, show an error and a back button instead of the form.
    if (!campaignData.campaignStatus || !MODIFIABLE_STATUSES.has(campaignData.campaignStatus)) {
        return (
            <ContentLayout header={pageHeader}>
                <SpaceBetween size="l">
                    <Alert type="warning" header={translate('status_not_modifiable_header')}>
                        {translate('status_not_modifiable_message')}
                    </Alert>
                    <Button variant="link" onClick={handleCancel}>
                        {translate('cancel_button')}
                    </Button>
                </SpaceBetween>
            </ContentLayout>
        );
    }

    return (
        <ContentLayout header={pageHeader}>
            <SpaceBetween size="l">
                {/* Campaign Status Section — above the form */}
                <CampaignStatusSection campaignData={campaignData} />

                {versionConflict && (
                    <Alert type="warning" header={translate('version_conflict_header')}>
                        <SpaceBetween size="s">
                            <Box>{submitError}</Box>
                            <Button
                                onClick={() => {
                                    trackClick('ReloadVersion', { page: PAGE_IDS.MODIFY_CAMPAIGN, campaignType });
                                    reloadCampaign();
                                }}
                            >
                                {translate('reload_latest_version_button')}
                            </Button>
                        </SpaceBetween>
                    </Alert>
                )}

                {submitError && !versionConflict && (
                    <Alert type="error" header={translate('error_header')}>
                        {submitError}
                    </Alert>
                )}

                {conflictingCampaignASINs.length > 0 && (
                    <Alert type="warning" header={translate('conflict_header')}>
                        <ConflictTable conflicts={conflictingCampaignASINs} />
                    </Alert>
                )}

                <CampaignForm
                    mode="modify"
                    form={form}
                    config={config}
                    campaignType={campaignType}
                    showMerchantGroup={showMerchantGroup}
                    errors={translatedFieldErrors}
                    markTouched={markTouched}
                    campaignStatus={campaignData.campaignStatus}
                />

                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={handleCancel}>
                        {translate('cancel_button')}
                    </Button>
                    <Button
                        variant="primary"
                        loading={submitting}
                        onClick={async () => {
                            trackClick('SubmitCampaign', {
                                page: PAGE_IDS.MODIFY_CAMPAIGN,
                                campaignType,
                            });
                            try {
                                await submitModification();
                            } catch (e) {
                                // Error state is normally set inside the hook; log unexpected throws for debugging
                                logError('Campaign modification failed unexpectedly', {
                                    error: e instanceof Error ? e.message : String(e),
                                });
                            } finally {
                                window.scrollTo(0, 0);
                            }
                        }}
                    >
                        {translate('modify_button')}
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        </ContentLayout>
    );
};

export default ModifyCampaignPage;
